/**
 * Client-side image processing: resize, convert to WebP, and strip EXIF metadata.
 * Canvas redraw naturally removes all EXIF/GPS metadata from the output.
 */

const PRESETS = {
  avatar:     { maxWidth: 512,  maxHeight: 512  },
  cover:      { maxWidth: 1600, maxHeight: 900  },
  post:       { maxWidth: 1920, maxHeight: 1920 },
  plan:       { maxWidth: 1200, maxHeight: 1200 },
  blog_cover: { maxWidth: 1600, maxHeight: 900  },
  og:         { maxWidth: 1200, maxHeight: 630  },
  default:    { maxWidth: 1920, maxHeight: 1920 },
};

/**
 * Process an image file before upload.
 * - Resizes to fit within maxWidth × maxHeight (preserving aspect ratio)
 * - Converts to WebP (falls back to JPEG)
 * - Strips all EXIF/GPS metadata via canvas redraw
 *
 * @param {File|Blob} file       - The image file to process
 * @param {object}    [options]
 * @param {string}    [options.preset]    - One of: avatar, cover, post, plan
 * @param {number}    [options.maxWidth]  - Override max width
 * @param {number}    [options.maxHeight] - Override max height
 * @param {number}    [options.quality]   - WebP/JPEG quality 0–1 (default 0.82)
 * @returns {Promise<{ blob: Blob, type: string }>}
 */
export async function processImage(file, options = {}) {
  // Skip non-images and GIFs (preserve animation)
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return { blob: file, type: file.type };
  }

  const preset = PRESETS[options.preset] || PRESETS.default;
  const maxWidth  = options.maxWidth  || preset.maxWidth;
  const maxHeight = options.maxHeight || preset.maxHeight;
  const quality   = options.quality   || 0.82;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    // Calculate scaled dimensions maintaining aspect ratio
    let newWidth = width;
    let newHeight = height;
    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      newWidth  = Math.round(width * ratio);
      newHeight = Math.round(height * ratio);
    }

    const canvas = document.createElement('canvas');
    canvas.width  = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
    bitmap.close();

    // Try WebP first, fall back to JPEG
    const webpBlob = await canvasToBlob(canvas, 'image/webp', quality);
    if (webpBlob && webpBlob.size > 0) {
      return { blob: webpBlob, type: 'image/webp' };
    }

    const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (jpegBlob && jpegBlob.size > 0) {
      return { blob: jpegBlob, type: 'image/jpeg' };
    }

    // If all else fails, return original
    return { blob: file, type: file.type };
  } catch {
    // Processing failed (e.g. HEIC on unsupported browser) — return original
    return { blob: file, type: file.type };
  }
}

function canvasToBlob(canvas, type, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}
